import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api, formatError } from "../lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to Lyon's Den. I'm the Spirit Guide — ask me about readings, products, house calls, or anything else." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: formatError(err) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#8B0000] hover:bg-[#A50000] text-white shadow-lg shadow-black/40 flex items-center justify-center border border-[#D4AF37]/50"
        aria-label="Open chat"
        data-testid="chat-toggle-btn"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 w-[90vw] max-w-[380px] h-[520px] bg-[#0A0A0A] border border-white/10 shadow-2xl flex flex-col"
          data-testid="chat-panel"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#111]">
            <div>
              <div className="text-sm font-serif">Lyon's Den Spirit Guide</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Always here to help</div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-discreet" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`text-sm leading-relaxed ${m.role === "user" ? "text-right" : ""}`}
              >
                <div
                  className={`inline-block px-3 py-2 max-w-[85%] ${
                    m.role === "user"
                      ? "bg-[#8B0000] text-white"
                      : "bg-[#1a1a1a] text-white/90 border border-white/5"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-xs text-white/50 italic">Consulting the spirit guide…</div>
            )}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2" data-testid="chat-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              className="flex-1 bg-transparent border-b border-white/20 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="text-[#D4AF37] hover:text-white"
              data-testid="chat-send-btn"
            >
              <Send size={18} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
